import { Injectable } from '@angular/core';
import { environment } from '../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { map } from 'rxjs';
import { KeycloakService } from 'keycloak-angular';
import { SignIn, SignUp } from '../model/auth.model';
import { VerifyingEmailComponent } from '../components/modals/verifying-email/verifying-email.component';
import { ModalService } from './modal.service';
import { IError } from '../interfaces';
import { DEFAULT_ERROR, ERROR_MESSAGES, activeTranslations } from '../enum/constants';
import { NotificationService } from './notifications.service';
import { ActivatedRoute, Router } from '@angular/router';
import { OtpComponent } from '../components/modals/otp/otp.component';


export interface InvitedUser {
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  phone?: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly typeToken = 'Bearer';
  private readonly tokenKey = 'access_key';

  constructor(
    private readonly router: Router,
    private readonly http: HttpClient,
    private readonly keycloakService: KeycloakService,
    private readonly modalService: ModalService,
    private readonly notificationService: NotificationService,
    private readonly activatedRoute: ActivatedRoute,
  ) {
  }

  urlLogin = `${window.location.protocol}//auth.${environment.web_url}/login`;

  get isLogged(): boolean {
    return !!localStorage.getItem(this.tokenKey);
  }

  public initUtm() {
    const utm_source = this.activatedRoute.snapshot.queryParams['utm_source'];

    if (utm_source) {
      this.router.navigate([], {
        queryParams: {
          'utm_source': null,
        },
        queryParamsHandling: 'merge',
      });
      localStorage.setItem('utm_source', utm_source);
    }
  }

  public login(payload: SignIn) {
    return this.http.post(environment.baseURL + '/sign-in/', payload)
      .pipe(map((res: any) => res.data));
  }

  public register(payload: SignUp) {
    return this.http.post(environment.baseURL + '/sign-up', payload)
      .pipe(map((res: any) => res.data));
  }

  /** Data of an invited user, used to prefill the sign-up form from the invite link (`?user_id=`) */
  public getInvitedUser(userId: string) {
    return this.http.get<any>(environment.baseURL + `/users/${userId}`)
      .pipe(map((res) => res.data as InvitedUser));
  }

  public openVerifyEmailModal(email: string) {
    const dialogData = {
      title: 'verifying_email.title',
      translate: true,
      component: VerifyingEmailComponent,
      data: {
        email: email,
      },
    };

    this.modalService.openModal(dialogData);
  }

  public openVerifyPhoneModal(email: string, password: string) {
    const dialogData = {
      title: 'verifying_phone.title',
      translate: true,
      component: OtpComponent,
      data: {
        email: email,
        password: password,
      },
    };

    this.modalService.openModal(dialogData);
  }

  public sendVerifyEmail(email: string) {
    const payload = {
      email,
    };
    return this.http.put(environment.baseURL + '/send-verify-email', payload);
  }

  public sendVerifyPhone(email: string) {
    const payload = {
      email: email,
    };
    return this.http.post(environment.baseURL + '/otp', payload);
  }

  public sendVerifyOtp(body: any) {
    return this.http.post(environment.baseURL + '/otp-verify', body);
  }

  public resetPassword(email: string) {
    const payload = {
      email,
    };
    return this.http.put(environment.baseURL + '/reset-password', payload);
  }

  public logout(redirectUri?: string) {
    const language = localStorage.getItem('language');
    localStorage.clear();
    if (language) {
      localStorage.setItem('language', language);
    }
    const headers = {
      'RefreshToken': this.keycloakService.getKeycloakInstance().refreshToken || '',
    };
    this.http.post(environment.baseURL + '/logout', {}, { headers }).subscribe({
      next: () => {
        window.location.href = redirectUri || this.urlLogin;
      },
      error: (e) => {
        window.location.href = redirectUri || this.urlLogin;
        (e.error?.errors || []).forEach((err: IError) => {
          this.notificationService.showError(activeTranslations.notifications_texts.error.error, ERROR_MESSAGES[err.code] || ERROR_MESSAGES[DEFAULT_ERROR]);
        });
      },
    });
  }

  public getCompanies() {
    return this.http.get<any>(environment.baseURL + '/companies/');
  }

  public createCompany(promoCode?: string) {
    return this.http.post<any>(environment.baseURL + '/companies/', promoCode ? { promo_code: promoCode } : {});
  }

  public getAuthorizationToken() {
    const token = localStorage.getItem(this.tokenKey);
    if (!token) return '';
    return [this.typeToken, token].join(' ');
  }

  public getSchema() {
    return 'global';
  }

  public getToken() {
    const token = localStorage.getItem(this.tokenKey) || null;
    return token;
  }

  public setToken(token: string) {
    localStorage.setItem(this.tokenKey, token);
  }


  authorization() {
    return this.http.get(environment.baseURL + '/authorization/');
  }

  public toLogin() {
    this.keycloakService.login({ redirectUri: this.urlLogin });
  }

  public toRegister() {
    this.keycloakService.register({ redirectUri: this.urlLogin });
  }
}
